import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const API = '/api';
function hdr() { return { Authorization: `Bearer ${localStorage.getItem('token')}` }; }

interface BLFMember {
  extension: string;
  display_name: string;
  state: 'ringing' | 'in_call' | 'available' | 'unavailable' | 'unknown';
  caller_num?: string;
  caller_name?: string;
  wait_secs?: number;
  unique_id?: string;
  group_id?: number;
  group_name?: string;
  answered_at?: string;
}

interface PickupLog {
  id: number;
  picking_extension: string;
  ringing_extension: string;
  caller_num: string;
  caller_name: string;
  wait_seconds: number;
  picked_up_at: string;
}

interface Props {
  user: any;
  onDial: (num: string) => void;           // triggers Dialer to call this number
  isCollapsed: boolean;
  onToggle: () => void;
  themeAccent?: string;
  themeDark?: boolean;
}

const STATE_CFG: Record<string, { label: string; color: string; bg: string; pulse: boolean }> = {
  ringing:     { label: 'Ringing',     color: '#f59e0b', bg: '#fef3c7', pulse: true  },
  in_call:     { label: 'In Call',     color: '#ef4444', bg: '#fee2e2', pulse: false },
  available:   { label: 'Available',   color: '#22c55e', bg: '#f0fdf4', pulse: false },
  unavailable: { label: 'Offline',     color: '#94a3b8', bg: '#f1f5f9', pulse: false },
  unknown:     { label: 'Unknown',     color: '#94a3b8', bg: '#f1f5f9', pulse: false },
};

function fmtWait(secs: number) {
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function fmtDuration(secs: number) {
  const m = Math.floor(secs / 60), s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function ringTone(play: boolean) {
  if (typeof window === 'undefined') return;
  try {
    const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
    if (!play) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine'; o.frequency.setValueAtTime(800, ctx.currentTime);
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.01);
    g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4);
    o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.4);
  } catch (_) {}
}

export default function BLFPickupPanel({ user, onDial, isCollapsed, onToggle, themeAccent = '#6366f1', themeDark = false }: Props) {
  const [members, setMembers] = useState<BLFMember[]>([]);
  const [log, setLog] = useState<PickupLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickingUp, setPickingUp] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [activeTab, setActiveTab] = useState<'blf' | 'log'>('blf');
  const [callTimers, setCallTimers] = useState<Record<string, number>>({});
  const prevRingingRef = useRef<Set<string>>(new Set<string>());
  const soundEnabledRef = useRef(true);
  const timerRef = useRef<any>(null);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const loadBLF = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pickup-groups/blf`, { headers: hdr() });
      const newMembers: BLFMember[] = r.data.members || [];
      const ringingArr = newMembers.filter(m => m.state === 'ringing').map(m => m.extension); const ringingNow = new Set<string>(ringingArr);
      // play ring sound for newly ringing extensions
      ringingNow.forEach(ext => {
        if (!prevRingingRef.current.has(ext) && soundEnabledRef.current) ringTone(true);
      });
      prevRingingRef.current = ringingNow;
      setMembers(newMembers);
    } catch (_) {}
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pickup-groups/log?limit=20`, { headers: hdr() });
      setLog(r.data.log || []);
    } catch (_) {}
  }, []);

  // Poll BLF state every 3s
  useEffect(() => {
    if (isCollapsed) return;
    loadBLF();
    const t = setInterval(loadBLF, 3000);
    return () => clearInterval(t);
  }, [isCollapsed, loadBLF]);

  // Timer tick for wait/duration counters
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCallTimers(prev => {
        const next = { ...prev };
        members.forEach(m => {
          if (m.state === 'ringing') next[m.extension] = (next[m.extension] || 0) + 1;
          else delete next[m.extension];
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [members]);

  // WebSocket BLF updates
  useEffect(() => {
    const wsUrl = 'wss://' + window.location.host + '/ws-live';
    let ws: WebSocket;
    let reconnTimer: any;

    function connect() {
      try {
        ws = new WebSocket(wsUrl);
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'blf_update') {
              const upd: Partial<BLFMember> & { extension: string } = msg.payload;
              setMembers(prev => {
                const idx = prev.findIndex(m => m.extension === upd.extension);
                if (idx < 0) return prev;
                const next = [...prev];
                next[idx] = { ...next[idx], ...upd };
                // ring sound on new ringing event
                if (upd.state === 'ringing' && !prevRingingRef.current.has(upd.extension) && soundEnabledRef.current) {
                  ringTone(true);
                  const arr = Array.from(prevRingingRef.current); arr.push(upd.extension); prevRingingRef.current = new Set(arr);
                } else if (upd.state !== 'ringing') {
                  const arr2 = Array.from(prevRingingRef.current).filter(x => x !== upd.extension); prevRingingRef.current = new Set<string>(arr2);
                }
                return next;
              });
            }
          } catch (_) {}
        };
        ws.onclose = () => { reconnTimer = setTimeout(connect, 5000); };
        ws.onerror = () => { try { ws.close(); } catch (_) {} };
      } catch (_) {}
    }
    connect();
    return () => { clearTimeout(reconnTimer); try { ws?.close(); } catch (_) {} };
  }, []);

  const pickup = async (m: BLFMember) => {
    setPickingUp(m.extension);
    try {
      // Primary: SIP dial via Dialer (most reliable for WebRTC)
      onDial(`*88${m.extension}`);
      // Also log via API
      await axios.post(`${API}/pickup-groups/pickup`, {
        ringing_extension: m.extension,
        pickup_method: 'sip',
      }, { headers: hdr() }).catch(() => {});
      showToast(`Picking up call from ${m.caller_name || m.caller_num || m.extension}...`);
      setTimeout(loadBLF, 1000);
      setTimeout(loadLog, 2000);
    } catch (e: any) {
      showToast('Pickup failed: ' + (e?.response?.data?.error || e.message), false);
    } finally {
      setTimeout(() => setPickingUp(null), 2000);
    }
  };

  const ringing = members.filter(m => m.state === 'ringing');
  const inCall  = members.filter(m => m.state === 'in_call');
  const others  = members.filter(m => m.state !== 'ringing' && m.state !== 'in_call');

  const panelBg  = themeDark ? '#1e293b' : '#ffffff';
  const panelBdr = themeDark ? '#334155' : '#e5e7eb';
  const panelTxt = themeDark ? '#f1f5f9' : '#1e293b';
  const panelMut = themeDark ? '#94a3b8' : '#64748b';
  const panelHov = themeDark ? '#0f172a' : '#f8fafc';

  if (isCollapsed) {
    return (
      <button onClick={onToggle} style={{
        position: 'relative', width: 40, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 4,
        background: themeDark ? '#1e293b' : '#f8fafc',
        borderLeft: `1px solid ${panelBdr}`, border: 'none', cursor: 'pointer',
        padding: '12px 0',
      }}>
        {ringing.length > 0 && (
          <span style={{
            position: 'absolute', top: 8, right: 6, background: '#f59e0b',
            color: '#fff', borderRadius: '50%', width: 16, height: 16,
            fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'ce-pulse 1s ease-in-out infinite',
          }}>{ringing.length}</span>
        )}
        <span style={{ fontSize: 18 }}>👥</span>
        <span style={{ fontSize: 9, color: panelMut, writingMode: 'vertical-rl' as const, transform: 'rotate(180deg)', letterSpacing: 0.5, fontWeight: 600 }}>GROUP</span>
      </button>
    );
  }

  return (
    <div style={{
      width: 280, display: 'flex', flexDirection: 'column', flexShrink: 0,
      background: panelBg, borderLeft: `1px solid ${panelBdr}`, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px', borderBottom: `1px solid ${panelBdr}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>👥</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: panelTxt }}>Group Pickup</span>
          {ringing.length > 0 && (
            <span style={{
              background: '#f59e0b', color: '#fff', borderRadius: 10,
              padding: '1px 7px', fontSize: 11, fontWeight: 700,
              animation: 'ce-pulse 1s ease-in-out infinite',
            }}>{ringing.length}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            title={soundEnabledRef.current ? 'Mute ring alerts' : 'Unmute ring alerts'}
            onClick={() => { soundEnabledRef.current = !soundEnabledRef.current; }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 2 }}
          >🔔</button>
          <button onClick={onToggle} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: panelMut, padding: 2 }}>›</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${panelBdr}`, flexShrink: 0 }}>
        {(['blf', 'log'] as const).map(tab => (
          <button key={tab} onClick={() => { setActiveTab(tab); if (tab === 'log') loadLog(); }}
            style={{
              flex: 1, padding: '8px 0', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: activeTab === tab ? panelBg : panelHov,
              color: activeTab === tab ? themeAccent : panelMut,
              borderBottom: activeTab === tab ? `2px solid ${themeAccent}` : '2px solid transparent',
            }}>
            {tab === 'blf' ? '📶 Live' : '📋 History'}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {activeTab === 'blf' && (
          <div>
            {/* Ringing calls — priority section */}
            {ringing.length > 0 && (
              <div>
                <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>
                  🔔 Ringing ({ringing.length})
                </div>
                {ringing.map(m => {
                  const waitSecs = (m.wait_secs || 0) + (callTimers[m.extension] || 0);
                  return (
                    <div key={m.extension} style={{
                      margin: '6px 10px', borderRadius: 10, overflow: 'hidden',
                      border: '2px solid #f59e0b', background: '#fffbeb',
                      boxShadow: '0 0 0 3px rgba(245,158,11,.15)',
                      animation: 'ce-ring-glow 1.5s ease-in-out infinite',
                    }}>
                      <div style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <div style={{
                              width: 8, height: 8, borderRadius: '50%', background: '#f59e0b',
                              animation: 'ce-pulse 0.8s ease-in-out infinite', flexShrink: 0,
                            }} />
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Ext {m.extension}</span>
                            {m.display_name && <span style={{ fontSize: 11, color: '#92400e' }}>{m.display_name}</span>}
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', fontVariantNumeric: 'tabular-nums' }}>
                            ⏱ {fmtWait(waitSecs)}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>
                          📞 {m.caller_name || 'Unknown Caller'}
                        </div>
                        {m.caller_num && <div style={{ fontSize: 11, color: '#92400e', marginBottom: 8 }}>{m.caller_num}</div>}
                        {m.group_name && <div style={{ fontSize: 10, color: '#b45309', marginBottom: 6 }}>Group: {m.group_name}</div>}
                        <button
                          onClick={() => pickup(m)}
                          disabled={pickingUp === m.extension}
                          style={{
                            width: '100%', padding: '9px 0', borderRadius: 8, border: 'none',
                            cursor: pickingUp === m.extension ? 'not-allowed' : 'pointer',
                            background: pickingUp === m.extension ? '#d97706' : '#f59e0b',
                            color: '#1a0f00', fontSize: 13, fontWeight: 800,
                            letterSpacing: 0.3, transition: 'all .15s',
                          }}
                        >
                          {pickingUp === m.extension ? '⏳ Connecting...' : '📲 Pick Up'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* In Call section */}
            {inCall.length > 0 && (
              <div>
                <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>
                  🔴 In Call ({inCall.length})
                </div>
                {inCall.map(m => (
                  <div key={m.extension} style={{
                    margin: '4px 10px', padding: '8px 10px', borderRadius: 8,
                    border: `1px solid #fecaca`, background: '#fef2f2',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', flex: 1 }}>
                        {m.display_name || `Ext ${m.extension}`}
                      </span>
                      <span style={{ fontSize: 10, color: '#991b1b' }}>In Call</span>
                    </div>
                    {m.caller_num && <div style={{ fontSize: 11, color: '#7f1d1d', marginTop: 2, marginLeft: 15 }}>{m.caller_name || m.caller_num}</div>}
                    <button onClick={() => onDial(m.extension)} style={{
                      marginTop: 6, marginLeft: 15, padding: '3px 10px', borderRadius: 6,
                      border: `1px solid #fca5a5`, background: 'transparent', cursor: 'pointer',
                      fontSize: 11, color: '#991b1b', fontWeight: 600,
                    }}>Call Extension</button>
                  </div>
                ))}
              </div>
            )}

            {/* Available / Offline */}
            {others.length > 0 && (
              <div>
                <div style={{ padding: '8px 14px 4px', fontSize: 11, fontWeight: 700, color: panelMut, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>
                  Members ({others.length})
                </div>
                {others.map(m => {
                  const cfg = STATE_CFG[m.state] || STATE_CFG.unknown;
                  return (
                    <div key={m.extension} style={{
                      margin: '3px 10px', padding: '7px 10px', borderRadius: 8,
                      border: `1px solid ${panelBdr}`, background: panelHov,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: panelTxt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                          {m.display_name || `Ext ${m.extension}`}
                        </div>
                        <div style={{ fontSize: 10, color: panelMut }}>{cfg.label}</div>
                      </div>
                      {m.state === 'available' && (
                        <button onClick={() => onDial(m.extension)} style={{
                          padding: '3px 8px', borderRadius: 6, border: `1px solid ${panelBdr}`,
                          background: 'transparent', cursor: 'pointer', fontSize: 11, color: panelMut,
                        }}>📞</button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {members.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: panelMut }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>No group members</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>Ask your admin to add you to a pickup group</div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'log' && (
          <div>
            {log.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: panelMut }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>No pickup history yet</div>
              </div>
            )}
            {log.map(entry => (
              <div key={entry.id} style={{
                margin: '4px 10px', padding: '8px 10px', borderRadius: 8,
                border: `1px solid ${panelBdr}`, background: panelHov,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: panelTxt }}>
                    {entry.caller_name || entry.caller_num || 'Unknown'}
                  </span>
                  <span style={{ fontSize: 10, color: panelMut }}>
                    {new Date(entry.picked_up_at).toLocaleTimeString()}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: panelMut }}>
                  {entry.caller_num && <span>{entry.caller_num} · </span>}
                  Waited {entry.wait_seconds || 0}s · Ext {entry.ringing_extension} → {entry.picking_extension}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'absolute' as const, bottom: 70, left: 10, right: 10,
          padding: '10px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
          background: toast.ok ? '#f0fdf4' : '#fef2f2',
          color: toast.ok ? '#166534' : '#991b1b',
          border: `1px solid ${toast.ok ? '#86efac' : '#fca5a5'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,.1)', zIndex: 10,
        }}>
          {toast.ok ? '✓' : '✗'} {toast.msg}
        </div>
      )}

      <style>{`
        @keyframes ce-ring-glow {
          0%, 100% { box-shadow: 0 0 0 3px rgba(245,158,11,.15); }
          50% { box-shadow: 0 0 0 6px rgba(245,158,11,.25); }
        }
      `}</style>
    </div>
  );
}
